import React, { useState, useRef, useEffect } from 'react';
import {
  ClockIcon,
  DocumentCheckIcon,
  CheckCircleIcon,
  PauseIcon,
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface ServiceRequestProgressFlowProps {
  currentState: string;
  activities?: any[];
  onStateChange?: (newState: string) => void;
  onReopen?: () => void;
  caseId?: string;
  assignedToId?: string | null;
  canEdit?: boolean;
}

const ServiceRequestProgressFlow: React.FC<ServiceRequestProgressFlowProps> = ({
  currentState,
  activities = [],
  onStateChange,
  onReopen,
  caseId: _caseId,
  assignedToId,
  canEdit = true
}) => {
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
    };

    if (showStatusDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusDropdown]);

  // Define the workflow states - Service Request: New -> In Progress -> Resolved -> Closed
  const workflowSteps = [
    {
      id: 'new',
      label: 'New',
      icon: ClockIcon,
      description: 'Request created'
    },
    {
      id: 'in_progress',
      label: 'In Progress',
      icon: DocumentCheckIcon,
      description: 'Work in progress'
    },
    {
      id: 'resolved',
      label: 'Resolved',
      icon: CheckCircleIcon,
      description: 'Request resolved'
    },
    {
      id: 'closed',
      label: 'Closed',
      icon: CheckIcon,
      description: 'Request closed',
      finalStates: ['closed', 'cancelled']
    }
  ];

  // Build list of states that were actually visited based on activity history
  const getVisitedStates = () => {
    const visited = new Set<string>();

    // Process state change activities in chronological order
    activities
      .filter(activity => activity.activityType === 'state_change' && activity.newState)
      .sort((a, b) => new Date(a.performedAt).getTime() - new Date(b.performedAt).getTime())
      .forEach(activity => {
        visited.add(activity.newState);
      });

    // Always include current state
    visited.add(currentState);

    return visited;
  };

  const visitedStates = getVisitedStates();

  // Map current state to workflow step
  const getStepStatus = (stepId: string, stepIndex: number) => {
    const currentStepIndex = getCurrentStepIndex();

    // Special handling for cancelled state
    if (currentState === 'cancelled') {
      if (stepId === 'closed') {
        return 'complete'; // Show the cancelled state in the closed position
      }
      // Only mark a step as complete if it was actually visited
      if (visitedStates.has(stepId)) {
        return 'complete';
      }
      return 'upcoming';
    }

    // Handle final states for closed step
    if (stepId === 'closed') {
      const finalStates = ['closed', 'cancelled'];
      if (finalStates.includes(currentState)) {
        return 'complete';
      }
      return stepIndex < currentStepIndex ? 'complete' : 'upcoming';
    }

    // Handle on_hold state
    if (currentState === 'on_hold') {
      // When on hold, show in_progress as current if it's the in_progress step
      if (stepId === 'in_progress') {
        return 'current';
      }
      // Previous steps are complete (just 'new')
      if (stepIndex < 1) {
        return 'complete';
      }
      return 'upcoming';
    }

    // Handle other states
    if (currentState === stepId) {
      return 'current';
    }

    if (stepIndex < currentStepIndex) {
      return 'complete';
    }

    return 'upcoming';
  };

  const getCurrentStepIndex = () => {
    const finalStates = ['closed', 'cancelled'];
    if (finalStates.includes(currentState)) {
      return workflowSteps.length - 1;
    }

    // Map on_hold to the state it was before
    if (currentState === 'on_hold') {
      // When on hold, show it at the in_progress step
      return 1; // Position at in_progress step
    }

    const index = workflowSteps.findIndex(step => step.id === currentState);
    return index >= 0 ? index : 0;
  };

  // Get appropriate icon and color for final state
  const getFinalStateInfo = () => {
    switch (currentState) {
      case 'cancelled':
        return { icon: XMarkIcon, color: 'text-red-600 bg-red-100', label: 'Cancelled' };
      case 'closed':
        return { icon: CheckIcon, color: 'text-gray-600 bg-gray-100', label: 'Closed' };
      case 'on_hold':
        return { icon: PauseIcon, color: 'text-yellow-600 bg-yellow-100', label: 'On Hold' };
      default:
        return null;
    }
  };


  return (
    <div className="bg-white shadow rounded-lg mb-6 p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Workflow Progress</h3>

      <nav aria-label="Progress">
        <ol className="flex items-center">
          {workflowSteps.map((step, stepIdx) => {
            const status = getStepStatus(step.id, stepIdx);
            const Icon = step.icon;
            const finalStateInfo = step.id === 'closed' && (currentState.startsWith('closed_') || currentState === 'closed' || currentState === 'cancelled') ? getFinalStateInfo() : null;
            const FinalIcon = finalStateInfo?.icon || Icon;

            return (
              <li key={step.id} className={stepIdx !== workflowSteps.length - 1 ? 'flex-1' : ''}>
                <div className="flex items-center">
                  <div className="relative flex h-10 w-10 items-center justify-center rounded-full">
                    {/* Step indicator */}
                    <div className={`
                      h-10 w-10 rounded-full flex items-center justify-center border-2
                      ${status === 'complete' ? (
                        finalStateInfo ? `${finalStateInfo.color} border-transparent` :
                        (currentState === 'cancelled' ? 'bg-white border-gray-300' : 'bg-indigo-600 border-transparent')
                      ) : status === 'current' ? 'bg-indigo-600 border-transparent' : 'bg-white border-gray-300'}
                    `}>
                      {status === 'complete' ? (
                        finalStateInfo ? (
                          <FinalIcon className="h-6 w-6" />
                        ) : currentState === 'cancelled' ? (
                          <Icon className="h-6 w-6 text-gray-400" />
                        ) : (
                          <CheckIcon className="h-6 w-6 text-white" />
                        )
                      ) : status === 'current' ? (
                        <Icon className="h-6 w-6 text-white" />
                      ) : (
                        <Icon className="h-6 w-6 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Connector line */}
                  {stepIdx !== workflowSteps.length - 1 && (
                    <div className={`ml-4 flex-1 h-0.5 ${
                      status === 'complete' ? (currentState === 'cancelled' ? 'bg-red-300' : 'bg-indigo-600') : 'bg-gray-300'
                    }`} />
                  )}
                </div>

                {/* Step label below icon */}
                <div className="mt-2">
                  <span className={`text-sm font-medium ${
                    status === 'complete' || status === 'current' ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    {finalStateInfo ? finalStateInfo.label : step.label}
                  </span>
                </div>
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Workflow Action Buttons - Show based on current state */}
      {canEdit && onStateChange && currentState !== 'closed' && currentState !== 'cancelled' && currentState !== 'resolved' && (
        <div className="mt-4 flex items-center justify-between border-t pt-4">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-700">Next Action:</span>
          </div>

          <div className="flex items-center space-x-2">
            {/* Actions based on current state */}
            {currentState === 'new' && (
              <>
                <button
                  onClick={() => onStateChange('in_progress')}
                  disabled={!assignedToId}
                  className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${
                    !assignedToId
                      ? 'bg-indigo-400 cursor-not-allowed opacity-60'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  Start Work
                </button>
                <button
                  onClick={() => onStateChange('cancelled')}
                  disabled={!assignedToId}
                  className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                    !assignedToId
                      ? 'border-red-200 text-red-400 bg-gray-50 cursor-not-allowed opacity-60'
                      : 'border-red-300 text-red-700 bg-white hover:bg-red-50'
                  }`}
                >
                  Cancel
                </button>
              </>
            )}

            {currentState === 'in_progress' && (
              <>
                <button
                  onClick={() => onStateChange('resolved')}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                >
                  Resolve
                </button>
                <button
                  onClick={() => onStateChange('cancelled')}
                  disabled={!assignedToId}
                  className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                    !assignedToId
                      ? 'border-red-200 text-red-400 bg-gray-50 cursor-not-allowed opacity-60'
                      : 'border-red-300 text-red-700 bg-white hover:bg-red-50'
                  }`}
                >
                  Cancel
                </button>
              </>
            )}

            {currentState === 'resolved' && (
              <>
                <button
                  onClick={() => onStateChange('closed')}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-gray-600 hover:bg-gray-700"
                >
                  Close
                </button>
                <button
                  onClick={() => onStateChange('cancelled')}
                  disabled={!assignedToId}
                  className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                    !assignedToId
                      ? 'border-red-200 text-red-400 bg-gray-50 cursor-not-allowed opacity-60'
                      : 'border-red-300 text-red-700 bg-white hover:bg-red-50'
                  }`}
                >
                  Cancel
                </button>
              </>
            )}

            {/* On Hold State - Resume to In Progress */}
            {currentState === 'on_hold' && (
              <>
                <button
                  onClick={() => onStateChange('in_progress')}
                  disabled={!assignedToId}
                  className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${
                    !assignedToId
                      ? 'bg-indigo-400 cursor-not-allowed opacity-60'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  Resume Work
                </button>
                <button
                  onClick={() => onStateChange('cancelled')}
                  disabled={!assignedToId}
                  className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                    !assignedToId
                      ? 'border-red-200 text-red-400 bg-gray-50 cursor-not-allowed opacity-60'
                      : 'border-red-300 text-red-700 bg-white hover:bg-red-50'
                  }`}
                >
                  Cancel
                </button>
              </>
            )}

            {/* On Hold Button - Only show when not already on hold */}
            {currentState !== 'on_hold' && (
              <button
                onClick={() => onStateChange('on_hold')}
                disabled={!assignedToId}
                className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium border ${
                  !assignedToId
                    ? 'bg-yellow-100 text-yellow-400 border-yellow-200 cursor-not-allowed opacity-60'
                    : 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100'
                } transition-colors`}
              >
                On Hold
              </button>
            )}
          </div>
        </div>
      )}

      {/* Additional state information */}
      {currentState === 'on_hold' && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            <PauseIcon className="inline-block h-4 w-4 mr-1" />
            This request is currently on hold
          </p>
        </div>
      )}

      {currentState === 'cancelled' && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">
            <XMarkIcon className="inline-block h-4 w-4 mr-1" />
            This request has been cancelled
          </p>
        </div>
      )}

      {currentState === 'resolved' && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center justify-between">
          <p className="text-sm text-green-800">
            <CheckCircleIcon className="inline-block h-4 w-4 mr-1" />
            This request has been resolved
          </p>
          {(onStateChange || onReopen) && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Change to:</span>
              {onStateChange && (
                <button
                  onClick={() => onStateChange('closed')}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-gray-600 hover:bg-gray-700"
                >
                  Closed
                </button>
              )}
              {onReopen && (
                <button
                  onClick={onReopen}
                  className="inline-flex items-center px-3 py-1.5 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-white hover:bg-blue-50"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-1" />
                  Reopen
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {(currentState.startsWith('closed_') || currentState === 'closed') && (
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
          <p className="text-sm text-gray-800">
            <CheckIcon className="inline-block h-4 w-4 mr-1" />
            This request has been closed
          </p>
        </div>
      )}
    </div>
  );
};

export default ServiceRequestProgressFlow;